import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Flame } from "lucide-react";
import { inr } from "@/lib/api";

export const CategoryCard = ({ cat, index }) => {
  const navigate = useNavigate();
  const disabled = cat.sold_out;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="group relative bg-[#111827] border border-white/10 hover:border-[#FF4D00] transition-colors duration-300 p-8 flex flex-col"
      data-testid={`category-card-${cat.id}`}
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            {cat.fast_filling && !cat.sold_out && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-[#F59E0B] border border-[#F59E0B]/40 px-2 py-1">
                <Flame className="h-3 w-3" /> Fast filling
              </span>
            )}
            {cat.sold_out && (
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#EF4444] border border-[#EF4444]/40 px-2 py-1">
                Sold out
              </span>
            )}
            {cat.requires_qualifier && (
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-white/50 border border-white/15 px-2 py-1">
                Qualifier
              </span>
            )}
          </div>
          <h3 className="font-display text-4xl tracking-tight uppercase leading-none mt-3">{cat.name}</h3>
          {cat.promo_highlight && (
            <span className="inline-block mt-2 bg-[#F59E0B] text-[#0b1120] text-[10px] uppercase tracking-widest font-bold px-2 py-1" data-testid={`promo-${cat.id}`}>{cat.promo_highlight}</span>
          )}
          <p className="text-xs uppercase tracking-[0.15em] text-white/40 mt-2">Min age {cat.min_age}+</p>
        </div>
        <span className="font-display text-5xl text-white/10 leading-none">{String(index + 1).padStart(2, "0")}</span>
      </div>

      <div className="mt-auto">
        <div className="mb-6" data-testid={`price-block-${cat.id}`}>
          {cat.original_price_inr > cat.price_inr && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.08 + 0.15 }}
              className="flex items-center gap-2 mb-1"
            >
              <span className="text-white/40 line-through text-lg">{inr(cat.original_price_inr)}</span>
              <span className="inline-flex items-center gap-1 bg-[#EF4444] text-white text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-0.5" data-testid={`discount-badge-${cat.id}`}>
                {Math.round((1 - cat.price_inr / cat.original_price_inr) * 100)}% OFF
              </span>
            </motion.div>
          )}
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 260, damping: 18, delay: index * 0.08 + 0.2 }}
            className="block font-display text-5xl text-[#FF4D00] leading-none"
          >
            {inr(cat.price_inr)}
          </motion.span>
        </div>

        <button
          disabled={disabled}
          onClick={() => navigate(`/register?category=${cat.id}`)}
          className={`w-full flex items-center justify-between px-6 py-4 text-xs uppercase tracking-[0.18em] font-bold transition-colors ${
            disabled
              ? "bg-white/5 text-white/30 cursor-not-allowed"
              : "bg-white text-[#0b1120] hover:bg-[#FF4D00] hover:text-white active:scale-[0.98]"
          }`}
          data-testid={`register-category-${cat.id}`}
        >
          {disabled ? "Sold out" : "Enter now"}
          {!disabled && <ArrowUpRight className="h-4 w-4" />}
        </button>
      </div>
    </motion.div>
  );
};
